/**
 * Turning a customer's spreadsheet into questions.
 *
 * Every customer sends the same questionnaire in a different shape: a title row
 * above the header, a numbering column, merged cells that arrive as blanks, an
 * "Auditor notes" column nobody fills in. The parsing happens in the browser
 * (see src/app/sheet.ts) so the file never reaches the server, but the decisions
 * — which column holds the questions, which rows are questions at all — are
 * here, where they can be tested without a spreadsheet.
 *
 * The guessing is deliberately conservative and always overridable in the UI. A
 * mapping that silently picks the wrong column produces a questionnaire full of
 * section headings answered with citations, which is worse than asking.
 */

export interface Cell {
  value: string;
}

/** A sheet as it arrives from SheetJS with `header: 1`: rows of raw cells. */
export type SheetRows = string[][];

export interface ExtractedQuestion {
  /** Index of the row in the original sheet, so the export can go back in place. */
  rowIndex: number;
  text: string;
}

export interface ExtractOptions {
  /** Zero-based column index holding the question text. */
  questionColumn: number;
  /** Rows before this index are skipped: titles, headers, instructions. */
  firstDataRow: number;
  /** Longest question we will accept, in characters. */
  maxLength?: number;
}

const DEFAULT_MAX_LENGTH = 2000;

/**
 * Words that mark a column as the one holding questions. Matched against the
 * header row, lowercased, as a substring: real questionnaires write "Question",
 * "Question / Requirement", "Control Description" and "Requirement Detail".
 */
const QUESTION_HEADER_HINTS = [
  'question',
  'requirement',
  'control',
  'inquiry',
  'description',
  'criteria',
  'ask',
];

/**
 * Columns that look like questions but are not: where the answer goes, who
 * signed off, what the auditor thought. Checked first, because "Response to
 * control question" contains a hint word and is emphatically not the question.
 */
const ANSWER_HEADER_HINTS = [
  'response',
  'answer',
  'reply',
  'comment',
  'note',
  'evidence',
  'status',
  'owner',
  'vendor',
];

function normalizeHeader(cell: string): string {
  return cell.trim().toLowerCase();
}

/**
 * The grouping key for a question.
 *
 * Lowercased and stripped of punctuation, so the same question arriving from
 * four customers with different capitalisation and a stray colon counts once.
 * The gap list and the answer history both key on this, and they have to agree:
 * a gap that disappears should be a question that now has an answer on record.
 */
export function questionKey(text: string): string {
  return normalizeText(text).toLowerCase().replace(/[^a-z0-9 ]/g, '');
}

/** Collapses runs of whitespace, including the newlines Excel puts inside a cell. */
export function normalizeText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Scores each column and returns the best guess, or -1 when nothing looks like
 * a question column and the customer has to choose.
 *
 * Two signals, in order: the header row's wording, then the shape of the data —
 * question columns hold long text in most rows, ID and Yes/No columns do not.
 */
export function guessQuestionColumn(rows: SheetRows, headerRow = 0): number {
  const width = rows.reduce((max, r) => Math.max(max, r.length), 0);
  if (width === 0) return -1;

  const header = rows[headerRow] ?? [];
  let best = -1;
  let bestScore = 0;

  for (let col = 0; col < width; col++) {
    const name = normalizeHeader(header[col] ?? '');
    let score = 0;

    if (ANSWER_HEADER_HINTS.some((h) => name.includes(h))) {
      // An answer column is never the question column, however question-like the
      // rest of its wording is.
      continue;
    }
    if (QUESTION_HEADER_HINTS.some((h) => name.includes(h))) score += 100;

    // The data itself: average length of non-empty cells below the header, and
    // how many rows are populated at all. A question column is long and dense.
    const body = rows.slice(headerRow + 1).map((r) => (r[col] ?? '').trim());
    const filled = body.filter((c) => c.length > 0);
    if (filled.length === 0) continue;

    const avgLength = filled.reduce((sum, c) => sum + c.length, 0) / filled.length;
    score += Math.min(avgLength, 120);
    score += (filled.length / body.length) * 20;

    if (score > bestScore) {
      bestScore = score;
      best = col;
    }
  }

  return best;
}

/**
 * Finds the header row: the first row whose cells look like column names rather
 * than content. Questionnaires often open with a title and a blank line, and
 * treating row 0 as the header then maps every column wrong.
 */
export function guessHeaderRow(rows: SheetRows): number {
  const limit = Math.min(rows.length, 10);
  for (let i = 0; i < limit; i++) {
    const cells = (rows[i] ?? []).map(normalizeHeader).filter((c) => c.length > 0);
    if (cells.length < 2) continue;
    const named = cells.some(
      (c) =>
        QUESTION_HEADER_HINTS.some((h) => c.includes(h)) ||
        ANSWER_HEADER_HINTS.some((h) => c.includes(h)),
    );
    if (named) return i;
  }
  // No recognisable header. The first row with two or more filled cells is the
  // best remaining guess, and the UI shows the mapping for confirmation anyway.
  for (let i = 0; i < limit; i++) {
    if ((rows[i] ?? []).filter((c) => c.trim().length > 0).length >= 2) return i;
  }
  return 0;
}

/**
 * Rejects rows that are structurally not questions.
 *
 * Section headings ("3. Access Control") and numbering artefacts otherwise
 * become questions that can never be answered from evidence, and then sit in the
 * gap list forever telling the customer to go write a policy about a heading.
 */
function isQuestionLike(text: string): boolean {
  if (text.length < 8) return false;
  // Pure numbering or lettering: "1.", "A)", "3.1.4".
  if (/^[\d.\s]+$/.test(text) || /^[a-z][).]\s*$/i.test(text)) return false;
  // A heading is short, has no sentence punctuation, and no verb-ish structure.
  // Requiring either a question mark or several words keeps "Do you encrypt
  // data at rest?" and "Describe your incident response process" and drops
  // "Access Control".
  if (text.includes('?')) return true;
  return text.split(/\s+/).length >= 4;
}

export function extractQuestions(rows: SheetRows, options: ExtractOptions): ExtractedQuestion[] {
  const { questionColumn, firstDataRow } = options;
  const maxLength = options.maxLength ?? DEFAULT_MAX_LENGTH;
  if (questionColumn < 0) return [];

  const out: ExtractedQuestion[] = [];
  for (let i = firstDataRow; i < rows.length; i++) {
    const raw = rows[i]?.[questionColumn] ?? '';
    const text = normalizeText(raw);
    if (!text || !isQuestionLike(text)) continue;
    out.push({ rowIndex: i, text: text.slice(0, maxLength) });
  }
  return out;
}

/**
 * The gap list: what to write next, ordered by how often it is asked.
 *
 * Grouping is by normalised text, so the same question arriving from four
 * customers with different capitalisation and trailing whitespace counts as one
 * gap asked four times. That count is the whole point of the screen — it turns
 * "we have 60 unanswered questions" into "write this one policy and 12 of them
 * go away".
 */
export interface GapInput {
  questionId: string;
  questionnaireId: string;
  customer: string;
  text: string;
  reason: string | null;
}

export interface Gap {
  /** Normalised, lowercased text — the grouping key. */
  key: string;
  /** The longest wording seen, which is usually the most complete one. */
  text: string;
  askedBy: string[];
  count: number;
  reasons: string[];
}

export function rankGaps(inputs: GapInput[]): Gap[] {
  const groups = new Map<string, Gap>();

  for (const input of inputs) {
    const text = normalizeText(input.text);
    const key = questionKey(input.text);
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        key,
        text,
        askedBy: [input.customer],
        count: 1,
        reasons: input.reason ? [input.reason] : [],
      });
      continue;
    }

    existing.count += 1;
    if (text.length > existing.text.length) existing.text = text;
    if (!existing.askedBy.includes(input.customer)) existing.askedBy.push(input.customer);
    if (input.reason && !existing.reasons.includes(input.reason)) existing.reasons.push(input.reason);
  }

  return [...groups.values()].sort((a, b) => b.count - a.count || a.text.localeCompare(b.text));
}
